import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Cog, Clock, DollarSign, Package, TrendingDown, CheckCircle, AlertTriangle, Wrench, ChevronDown, ChevronUp, Camera } from 'lucide-react';

function MeterPhotos({ cycleId }) {
  const [imgs, setImgs] = useState([]);
  useEffect(() => {
    api.get(`/uploads/by-cycle/${cycleId}`).then(r => setImgs((r.data || []).filter(x => x.is_meter_reading))).catch(() => {});
  }, [cycleId]);
  if (!imgs.length) return null;
  const base = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="col-span-2 sm:col-span-4" data-testid={`meter-photos-${cycleId}`}>
      <span className="text-slate-400 mb-1.5 flex items-center gap-1"><Camera size={11} /> Hour-Meter Photo Proof:</span>
      <div className="flex flex-wrap gap-3">
        {imgs.map((im) => (
          <a key={im.file_id} href={`${base}/api/uploads/files/${im.filename}`} target="_blank" rel="noreferrer" className="block" data-testid={`meter-photo-${im.file_id}`}>
            <div className="w-20 h-20 rounded-lg overflow-hidden border border-slate-200 hover:border-emerald-400 transition-colors">
              <img src={`${base}/api/uploads/files/${im.filename}`} alt={`${im.reading_type} meter`} className="w-full h-full object-cover" />
            </div>
            <p className="text-[10px] text-slate-600 mt-0.5 capitalize">{im.reading_type}: <span className="font-semibold text-slate-800">{(im.entered_value ?? im.ocr_value)?.toLocaleString?.() ?? (im.entered_value ?? im.ocr_value)}</span> hrs{im.ocr_value != null && im.ocr_value !== im.entered_value && <span className="text-slate-400 normal-case"> (OCR {im.ocr_value?.toLocaleString?.() ?? im.ocr_value})</span>}</p>
          </a>
        ))}
      </div>
    </div>
  );
}

function ReasonBadge({ reason }) {
  if (!reason) return null;
  const colors = {
    worn_out: 'bg-slate-100 text-slate-600', broken: 'bg-red-50 text-red-600',
    damaged: 'bg-orange-50 text-orange-600', trial_ended: 'bg-blue-50 text-blue-600',
    preventive_replacement: 'bg-emerald-50 text-emerald-600', unknown: 'bg-slate-100 text-slate-400',
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${colors[reason] || colors.unknown}`}>{reason.replace(/_/g, ' ')}</span>;
}

export default function MachineHistoryPage() {
  const { machineId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCycle, setExpandedCycle] = useState(null);

  useEffect(() => {
    api.get(`/cycles/machine-history/${machineId}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [machineId]);

  if (loading) return <div className="animate-pulse h-96 bg-slate-100 rounded-lg" />;
  if (!data) return <div className="text-center py-16 text-slate-400">Machine not found</div>;

  const { machine, site, company, cycles, summary, product_summary } = data;
  const reversedCycles = [...cycles].reverse();

  return (
    <div data-testid="machine-history-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to={`/analytics/machines/${machineId}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mb-2" data-testid="back-link"><ArrowLeft size={12} /> Back to Machine Detail</Link>
          <h1 className="text-2xl font-bold text-slate-900 font-heading" data-testid="page-title">{machine.brand} {machine.model} — Cycle History</h1>
          <p className="text-sm text-slate-500 mt-0.5">S/N: {machine.serial_number} | {site?.name} | {company?.name}</p>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Cycles', value: summary.total_cycles, icon: Wrench, color: 'text-slate-600 bg-slate-100' },
          { label: 'Active', value: summary.active_cycles, icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Closed', value: summary.closed_cycles, icon: Clock, color: 'text-blue-600 bg-blue-50' },
          { label: 'Avg Life', value: `${summary.avg_life_hours}h`, icon: Clock, color: 'text-violet-600 bg-violet-50' },
          { label: 'Avg ₹/Hr', value: `₹${summary.avg_cost_per_hour}`, icon: DollarSign, color: 'text-orange-600 bg-orange-50' },
          { label: 'Total Spend', value: `₹${summary.total_spend.toLocaleString()}`, icon: TrendingDown, color: 'text-red-600 bg-red-50' },
        ].map((k, i) => (
          <Card key={i} className="p-3 flex items-center gap-3">
            <div className={`p-2 rounded-lg ${k.color.split(' ')[1]}`}><k.icon size={16} className={k.color.split(' ')[0]} /></div>
            <div><p className="text-xs text-slate-500">{k.label}</p><p className="text-sm font-bold text-slate-900 font-heading" data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, '-')}`}>{k.value}</p></div>
          </Card>
        ))}
      </div>

      {/* Product Performance Summary */}
      {product_summary.length > 0 && (
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Product Performance on This Machine</h2>
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cycles</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Avg Life (hrs)</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Spend</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">₹/Hr</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {product_summary.map((p, i) => (
                <TableRow key={i} className={i === 0 && p.cost_per_hour > 0 ? 'bg-emerald-50/30' : ''}>
                  <TableCell className="font-medium text-slate-900 text-sm">{p.name}</TableCell>
                  <TableCell><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.type === 'casting' ? 'bg-blue-50 text-blue-600' : p.type === 'forging' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>{p.type || '-'}</span></TableCell>
                  <TableCell className="text-sm text-slate-600">{p.cycles}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.avg_life_hours}h</TableCell>
                  <TableCell className="text-sm font-medium">₹{p.total_cost.toLocaleString()}</TableCell>
                  <TableCell className="text-sm font-bold text-slate-900">{p.cost_per_hour > 0 ? `₹${p.cost_per_hour}` : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Timeline */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">Cycle Timeline</h2>
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-5 top-0 bottom-0 w-px bg-slate-200" />

          <div className="space-y-0">
            {reversedCycles.map((c, i) => {
              const isActive = c.status === 'active';
              const isExpanded = expandedCycle === c.cycle_id;
              return (
                <div key={c.cycle_id} data-testid={`cycle-${i}`} className="relative pl-12 pb-6">
                  {/* Timeline dot */}
                  <div className={`absolute left-3 top-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isActive ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-white'}`}>
                    {isActive ? <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> : <div className="w-2 h-2 rounded-full bg-slate-300" />}
                  </div>

                  {/* Cycle Card */}
                  <div className={`rounded-lg border p-4 cursor-pointer transition-all hover:shadow-sm ${isActive ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white hover:bg-slate-50/50'}`}
                    onClick={() => setExpandedCycle(isExpanded ? null : c.cycle_id)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isActive ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">ACTIVE</Badge> : <Badge variant="outline" className="text-slate-400 text-[10px]">CLOSED</Badge>}
                        <span className="text-xs text-slate-500">{c.installation_date}{c.closure_date ? ` → ${c.closure_date}` : ''}</span>
                        <ReasonBadge reason={c.closure_reason} />
                      </div>
                      <div className="flex items-center gap-4">
                        {c.cycle_life_hours > 0 && <span className="text-xs font-semibold text-slate-700"><Clock size={11} className="inline mr-1 text-slate-400" />{c.cycle_life_hours}h</span>}
                        {c.cost_per_hour > 0 && <span className="text-xs font-bold text-slate-900">₹{c.cost_per_hour}/hr</span>}
                        <span className="text-xs font-medium text-slate-600">₹{c.total_cost.toLocaleString()}</span>
                        {isExpanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                      </div>
                    </div>

                    {/* Product pills */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {c.entries.map((e, j) => (
                        <span key={j} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                          <Package size={10} className="text-slate-400" />
                          {e.product_name} <span className="text-slate-400">x{e.quantity}</span>
                        </span>
                      ))}
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div><span className="text-slate-400">Install Hours:</span><br /><span className="font-medium text-slate-700">{c.installation_hours?.toLocaleString()}</span></div>
                        {c.closure_hours && <div><span className="text-slate-400">Closure Hours:</span><br /><span className="font-medium text-slate-700">{c.closure_hours?.toLocaleString()}</span></div>}
                        <div><span className="text-slate-400">Total Qty:</span><br /><span className="font-medium text-slate-700">{c.total_qty} points</span></div>
                        {c.operator_remarks && <div className="col-span-2 sm:col-span-1"><span className="text-slate-400">Remarks:</span><br /><span className="font-medium text-slate-700">{c.operator_remarks}</span></div>}
                        {c.entries.length > 0 && (
                          <div className="col-span-2 sm:col-span-4">
                            <span className="text-slate-400 mb-1 block">Entry Breakdown:</span>
                            <div className="space-y-1">
                              {c.entries.map((e, j) => (
                                <div key={j} className="flex items-center gap-3 text-[11px]">
                                  <span className="font-medium text-slate-700">{e.product_name}</span>
                                  {e.supplier_name && <span className="text-slate-400">via {e.supplier_name}</span>}
                                  <span className="text-slate-500">x{e.quantity}</span>
                                  <span className="text-slate-500">₹{e.unit_price}/ea</span>
                                  <span className="font-semibold text-slate-700">= ₹{e.entry_cost.toLocaleString()}</span>
                                  {e.strata && <span className="text-slate-400">[{e.strata.replace(/_/g, ' ')}]</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <MeterPhotos cycleId={c.cycle_id} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {cycles.length === 0 && (
            <div className="text-center py-10 text-slate-400 pl-12">
              <Wrench size={24} className="mx-auto mb-2 text-slate-300" />
              No cycle history for this machine
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
